import { useQuery } from "@tanstack/react-query"

import {
  getAzureOverview,
  getDataSources,
  getHealthCheck,
  getNavigatorLibraries,
  getPerformanceStats,
  getSystemHealth,
  getTrainingStatus,
  type SystemHealthResponse,
} from "@/api/admin-api"

import { adminQueryKeys } from "../admin-query-keys"
import { FAST_STALE_TIME, GC_TIME, MEDIUM_STALE_TIME } from "./_shared"

export function useAdminTrainingStatusQuery(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.trainingStatus(),
    queryFn: () => getTrainingStatus(),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useAdminDataSourcesQuery(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.dataSources(),
    queryFn: () => getDataSources(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useAdminAzureOverviewQuery(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.azureOverview(),
    queryFn: () => getAzureOverview(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useAdminNavigatorLibrariesQuery(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.navigatorLibraries(),
    queryFn: () => getNavigatorLibraries(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useAdminPerformanceStatsQuery(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.performanceStats(),
    queryFn: () => getPerformanceStats(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

// System Health Hooks
export function useAdminSystemHealthQuery(refreshIntervalMs?: number) {
  // 0 = manual only (no auto-refresh), undefined = use default 10s. Without
  // this guard React Query treats `refetchInterval: 0` as "poll as fast as
  // possible" and storms the backend when the UI disables auto-refresh.
  const interval = refreshIntervalMs === 0 ? false : (refreshIntervalMs ?? 10000)

  return useQuery<SystemHealthResponse>({
    queryKey: ["admin", "system-health"],
    queryFn: getSystemHealth,
    refetchInterval: interval,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache
  })
}

export function useHealthCheckQuery(refreshIntervalMs?: number) {
  // 0 = manual only (no auto-refresh), undefined = use default 10s
  const interval = refreshIntervalMs === 0 ? false : (refreshIntervalMs ?? 10000)

  return useQuery({
    queryKey: ["health-check"],
    queryFn: getHealthCheck,
    refetchInterval: interval,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache
    enabled: true, // Always enable, even for manual refresh
  })
}
